// src/components/services/ServiceRailPanel.tsx
// Compact services panel for the Right Rail.
// Shows connected/available services for the current entity with connect/configure actions.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useProduct, useBillingBillingAccount, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { entityStore } from '@/stores/EntityStore';
import {
  SERVICE_REGISTRY,
  getAvailableServices,
  buildServiceBindings,
  getAllServicesAsAvailable,
} from '@/features/services/logic';
import type { ServiceBinding, ServiceSummaryItem } from '@/features/services/types';

// ---------------------------------------------------------------------------
// Compact service item for rail
// ---------------------------------------------------------------------------

function ServiceRailItem({
  serviceKey,
  title,
  description,
  binding,
  onConnect,
  onConfigure,
  t,
}: {
  serviceKey: string;
  title: string;
  description: string;
  binding?: ServiceBinding;
  onConnect: () => void;
  onConfigure: () => void;
  t: (k: string) => string;
}) {
  const isConnected = !!binding?.connected;
  const def = SERVICE_REGISTRY[serviceKey];
  const summary = isConnected && binding?.config && def ? def.getSummary(binding.config) : [];

  return (
    <div
      className={`rounded-lg border p-3 transition-all ${
        isConnected
          ? 'border-border bg-card'
          : 'border-dashed border-border/50 bg-muted/10'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {isConnected && (
            <span className="w-1.5 h-1.5 rounded-full bg-success flex-shrink-0" />
          )}
          <span className="text-sm font-medium text-foreground">{title}</span>
        </div>
        {isConnected ? (
          <button
            onClick={onConfigure}
            className="text-[11px] text-primary hover:text-primary/80 font-medium transition-colors"
          >
            {t('services.configure')}
          </button>
        ) : (
          <button
            onClick={onConnect}
            className="text-[11px] px-2 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {t('services.connect')}
          </button>
        )}
      </div>

      {!isConnected && (
        <p className="text-[11px] text-muted-foreground ml-3.5">{description}</p>
      )}

      {summary.length > 0 && (
        <div className="ml-3.5 mt-1 space-y-0.5">
          {summary.map((item: ServiceSummaryItem, i: number) => (
            <div key={i} className="text-[11px] text-muted-foreground">
              {t(item.labelKey)}: <span className="text-foreground/70">{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export const ServiceRailPanel = observer(() => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const documentStore = useDocument();
  const productStore = useProduct();
  const billingAccountStore = useBillingBillingAccount();

  const [bindings, setBindings] = useState<Record<string, ServiceBinding>>({});
  const [isLoading, setIsLoading] = useState(false);

  const entity = entityStore.currentEntity;
  const entityType = entity?.type || '';
  const entityId = entity?.id || '';

  // Load when entity context changes
  useEffect(() => {
    if (!entityId || !entityType) {
      setBindings({});
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        await productStore.ListProducts();

        let documentPolicy: any = null;
        try {
          documentPolicy = await documentStore.GetDocumentPolicyByEntity(entityType, entityId);
        } catch {
          // no policy yet
        }

        let billingConfig: any = null;
        try {
          await billingAccountStore.ListBillingAccounts();
          if (billingAccountStore.items.length > 0) {
            billingConfig = billingAccountStore.items[0];
          }
        } catch {
          // no billing yet
        }

        if (!cancelled) {
          setBindings(buildServiceBindings({ documentPolicy, billingConfig }));
        }
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [entityId, entityType, documentStore, productStore, billingAccountStore]);

  const tenantProducts = productStore.items || [];
  const matched = getAvailableServices(tenantProducts);
  // Fallback: if no products loaded or no slugs matched, show all services from registry
  const availableServices = matched.length > 0 ? matched : getAllServicesAsAvailable();

  const handleConnect = useCallback(async (serviceKey: string) => {
    if (!entityId) return;

    try {
      if (serviceKey === 'document') {
        await documentStore.CreateDocumentPolicy({
          name: `${entityType}-${entityId}-policy`,
          scope_entity_type: entityType,
          allowed_document_type_ids: [],
          retention_rules: [],
        } as any);

        const policy = await documentStore.GetDocumentPolicyByEntity(entityType, entityId);
        setBindings((prev) => ({
          ...prev,
          document: { slug: 'document', connected: true, config: policy },
        }));
      } else if (serviceKey === 'billing') {
        const account = await billingAccountStore.CreateBillingAccount({
          name: `${entityType}-${entityId}-billing`,
          email: '',
          phone: '',
        });
        setBindings((prev) => ({
          ...prev,
          billing: { slug: 'billing', connected: true, config: account },
        }));
      }
    } catch {
      // TODO: show error toast
    }
  }, [entityId, entityType, documentStore, billingAccountStore, scope.productId]);

  const handleConfigure = useCallback((serviceKey: string) => {
    const def = SERVICE_REGISTRY[serviceKey];
    if (def && entityType && entityId) {
      navigate(`/${entityType}s/${entityId}/services/${def.configRoute}`);
    }
  }, [entityId, entityType, navigate]);

  if (!entity) {
    return (
      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
        {t('layout.rail.noContext')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-foreground">
          {t('services.connectedServices')}
        </h3>
      </div>

      {availableServices.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">
          {t('services.noServicesAvailable')}
        </p>
      ) : (
        availableServices.map((service) => (
          <ServiceRailItem
            key={service.key}
            serviceKey={service.key}
            title={t(service.definition.titleKey)}
            description={t(service.definition.descriptionKey)}
            binding={bindings[service.key]}
            onConnect={() => handleConnect(service.key)}
            onConfigure={() => handleConfigure(service.key)}
            t={t}
          />
        ))
      )}
    </div>
  );
});
